import * as React from 'react';
import { getUnit } from '@/api/units';
import { Box, Stack, Container, Typography, Button, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { BuildingApartment as BuildingApartmentIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { Unit } from '@/types/v2/unit';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Seo } from '@/components/seo';

import { OwnersTable } from './components/owners-table';
import { UnitDetails } from './components/unit-details';
import { UnitRequests } from './components/unit-requests';

interface UnitResponse {
  unit: Unit;
}

export function UnitPage(): React.JSX.Element {
  const { community } = useUser();
  const { unitId } = useParams<{ unitId: string }>();
  const theme = useTheme();
  const dialog = useDialog();

  if (!unitId) {
    return <div>Unit ID is required</div>;
  }

  const { data, isLoading, error, refetch } = useQuery<UnitResponse>({
    queryKey: [`unit-${unitId}`, community?.id],
    queryFn: async () => {
      const unit = await getUnit(unitId);
      return { unit };
    },
  });
  const unit = data?.unit;

  // Show unit not found error
  if (!isLoading && error?.message === 'Unit not found') {
    return (
      <>
        <Seo title="Unit Not Found" />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            py: 3,
            backgroundColor: theme.palette.background.default,
            minHeight: '100vh',
          }}
        >
          <Container maxWidth="xl">
            <CommunityPageHeader
              breadcrumbs={[
                {
                  label: 'Home',
                  href: '/',
                },
                {
                  label: 'Units',
                  href: paths.units.index,
                },
                {
                  label: 'Unit Not Found',
                },
              ]}
              icon={<BuildingApartmentIcon size={36} />}
              subtitle="The unit you're looking for doesn't exist or has been removed"
              title="Unit Not Found"
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  color: 'primary.main',
                  opacity: 0.8,
                  mb: 4,
                }}
              >
                <BuildingApartmentIcon size={200} />
              </Box>

              <Typography color="text.secondary" variant="body1" sx={{ mb: 2 }}>
                Unit ID: <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>{unitId}</code>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                  sx={{ minWidth: 120 }}
                >
                  Go Back
                </Button>
                <Button
                  variant="contained"
                  href={paths.units.index}
                  sx={{ minWidth: 120 }}
                >
                  View All Units
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Seo title="Unit" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            breadcrumbs={[
              {
                label: 'Home',
                href: '/',
              },
              {
                label: 'Units',
                href: paths.units.index,
              },
              {
                label: unit?.address || 'Unit Details',
              },
            ]}
            icon={<BuildingApartmentIcon size={36} />}
            subtitle="View unit details, ownership information, and associated requests"
            title={unit?.address || 'Unit Details'}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ position: 'relative' }}>
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Grid container spacing={4}>
                {data ? (
                  <React.Fragment>
                    <Grid lg={4} xs={12}>
                      <UnitDetails onRefresh={refetch} unit={data.unit} />
                    </Grid>
                    <Grid lg={8} xs={12}>
                      <Stack spacing={4}>
                        <OwnersTable unitId={unitId} />
                        <UnitRequests communityId={community?.id || ''} unitId={unitId} />
                      </Stack>
                    </Grid>
                  </React.Fragment>
                ) : null}
              </Grid>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
}
